import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Cog6ToothIcon,
  CheckCircleIcon,
  ExclamationCircleIcon,
  BuildingOfficeIcon,
  CalendarIcon,
  DevicePhoneMobileIcon,
  DocumentTextIcon
} from '@heroicons/react/24/outline';
import { auth } from '../../../../firebase';
import { Select, MenuItem, FormControl, InputLabel } from '@mui/material';
import axios from 'axios';
import LanguageSwitcher from '../../../common/LanguageSwitcher';
import { useDriver } from '../../../../contexts/DriverContext';

const ConfigurationSection = ({ userData }) => {
  const { t } = useTranslation();
  const { driverAccount, refreshDriverAccount } = useDriver();

  // Romanian counties
  const romanianCounties = [
    { code: 'AB', name: 'Alba' },
    { code: 'AR', name: 'Arad' },
    { code: 'AG', name: 'Argeș' },
    { code: 'BC', name: 'Bacău' },
    { code: 'BH', name: 'Bihor' },
    { code: 'BN', name: 'Bistrița-Năsăud' },
    { code: 'BT', name: 'Botoșani' },
    { code: 'BR', name: 'Brăila' },
    { code: 'BV', name: 'Brașov' },
    { code: 'B', name: 'București' },
    { code: 'BZ', name: 'Buzău' },
    { code: 'CL', name: 'Călărași' },
    { code: 'CS', name: 'Caraș-Severin' },
    { code: 'CJ', name: 'Cluj' },
    { code: 'CT', name: 'Constanța' },
    { code: 'CV', name: 'Covasna' },
    { code: 'DB', name: 'Dâmbovița' },
    { code: 'DJ', name: 'Dolj' },
    { code: 'GL', name: 'Galați' },
    { code: 'GR', name: 'Giurgiu' },
    { code: 'GJ', name: 'Gorj' },
    { code: 'HR', name: 'Harghita' },
    { code: 'HD', name: 'Hunedoara' },
    { code: 'IL', name: 'Ialomița' },
    { code: 'IS', name: 'Iași' },
    { code: 'IF', name: 'Ilfov' },
    { code: 'MM', name: 'Maramureș' },
    { code: 'MH', name: 'Mehedinți' },
    { code: 'MS', name: 'Mureș' },
    { code: 'NT', name: 'Neamț' },
    { code: 'OT', name: 'Olt' },
    { code: 'PH', name: 'Prahova' },
    { code: 'SJ', name: 'Sălaj' },
    { code: 'SM', name: 'Satu Mare' },
    { code: 'SB', name: 'Sibiu' },
    { code: 'SV', name: 'Suceava' },
    { code: 'TR', name: 'Teleorman' },
    { code: 'TM', name: 'Timiș' },
    { code: 'TL', name: 'Tulcea' },
    { code: 'VL', name: 'Vâlcea' },
    { code: 'VS', name: 'Vaslui' },
    { code: 'VN', name: 'Vrancea' }
  ];

  const [formData, setFormData] = useState({
    shareInformation: userData?.information_sharing || false,
    legalStatus: userData?.legal_status || 'UNDEFINED'
  });

  // Invoice data state
  const [invoiceData, setInvoiceData] = useState({
    name: '',
    tax_identifier: '',
    reg_com: '',
    address: '',
    city: '',
    subdivision_code: '',
    country_code: 'RO',
    bank: '',
    iban: '',
    vatPayer: false
  });

  const [errors, setErrors] = useState({});
  const [successMessage, setSuccessMessage] = useState('');
  const [configSuccessMessage, setConfigSuccessMessage] = useState('');
  const [configErrors, setConfigErrors] = useState({});
  const [invoiceSubmitting, setInvoiceSubmitting] = useState(false);

  // Mobile navigation preference state
  const [mobileNavStyle, setMobileNavStyle] = useState(() => {
    return localStorage.getItem('mobileNavStyle') || 'hamburger';
  });

  // Update form data when userData changes
  useEffect(() => {
    setFormData({
      shareInformation: userData?.information_sharing || false,
      legalStatus: userData?.legal_status || 'UNDEFINED'
    });

    // Load invoice data from driver context
    if (driverAccount?.invoiceData) {
      setInvoiceData({
        name: driverAccount.invoiceData.name || '',
        tax_identifier: driverAccount.invoiceData.tax_identifier || '',
        reg_com: driverAccount.invoiceData.reg_com || '',
        address: driverAccount.invoiceData.address || '',
        city: driverAccount.invoiceData.city || '',
        subdivision_code: driverAccount.invoiceData.subdivision_code || '',
        country_code: driverAccount.invoiceData.country_code || 'RO',
        bank: driverAccount.invoiceData.bank || '',
        iban: driverAccount.invoiceData.iban || '',
        vatPayer: driverAccount.invoiceData.vatPayer || false
      });
    }
  }, [userData, driverAccount]);

  // Helper function to map API status to display value
  const getLegalStatusDisplay = (status) => {
    const statusMap = {
      'INDIVIDUAL': t('settings.configuration.legalStatus.naturalPerson'),
      'LEGAL_ENTITY': t('settings.configuration.legalStatus.legalEntity')
    };
    return statusMap[status] || '';
  };

  // Helper function to map API status to UI value
  const getUILegalStatus = (apiStatus) => {
    const statusMap = {
      'INDIVIDUAL': 'persoana_fizica',
      'LEGAL_ENTITY': 'persoana_juridica'
    };
    return statusMap[apiStatus] || '';
  };

  // Helper function to map display value to API status
  const getAPILegalStatus = (displayValue) => {
    const statusMap = {
      'persoana_fizica': 'INDIVIDUAL',
      'persoana_juridica': 'LEGAL_ENTITY'
    };
    return statusMap[displayValue];
  };

  const updateDriverConfig = async (configData) => {
    setConfigSuccessMessage('');
    setConfigErrors({});

    try {
      const idToken = await auth.currentUser.getIdToken(true);

      // Prepare update payload
      const updatePayload = {
        customer_subtype: configData.legalStatus,
        information_sharing: configData.informationSharing
      };

      // Include invoice details if available
      if (configData.invoiceData) {
        updatePayload.invoiceData = configData.invoiceData;
      }

      const response = await axios.patch('https://api.servicegest.ro/driver/update',
        updatePayload,
        {
          headers: {
            'Authorization': `Bearer ${idToken}`,
            'Content-Type': 'application/json'
          }
        }
      );

      if (response.status === 200) {
        setFormData({
          legalStatus: configData.legalStatus,
          shareInformation: configData.informationSharing
        });

        // Refresh driver account to get updated data
        await refreshDriverAccount();

        // Show success message
        setConfigSuccessMessage(
          configData.legalStatus !== formData.legalStatus
            ? t('settings.configuration.legalStatusUpdated')
            : t('settings.configuration.sharingUpdated')
        );
      }
    } catch (error) {
      console.error('Error updating driver config:', error);
      setConfigErrors({
        config: t('settings.configuration.errorMessage')
      });
    }
  };

  // Update information sharing handler
  const handleInformationSharingChange = (e) => {
    const { checked } = e.target;
    updateDriverConfig({
      informationSharing: checked,
      legalStatus: formData.legalStatus === 'UNDEFINED' ? null : formData.legalStatus,
      invoiceDetails: invoiceData.name ? invoiceData : undefined
    });
    setFormData(prev => ({
      ...prev,
      shareInformation: checked
    }));
  };

  // Update legal status change handler
  const handleLegalStatusChange = (event) => {
    const apiStatus = getAPILegalStatus(event.target.value);
    updateDriverConfig({
      legalStatus: apiStatus,
      informationSharing: formData.shareInformation,
      invoiceDetails: invoiceData.name ? invoiceData : undefined
    });
    setFormData(prev => ({
      ...prev,
      legalStatus: apiStatus
    }));
  };

  // Handle Google Calendar integration
  const handleGoogleCalendarIntegration = () => {
    console.log('Google Calendar integration clicked');
  };

  // Handle mobile navigation style change
  const handleMobileNavChange = (event) => {
    const newStyle = event.target.value;
    setMobileNavStyle(newStyle);
    localStorage.setItem('mobileNavStyle', newStyle);
    // Trigger a custom event to notify the parent component
    window.dispatchEvent(new CustomEvent('mobileNavStyleChange', { detail: newStyle }));
  };

  // Handle invoice data change
  const handleInvoiceDataChange = (field, value) => {
    setInvoiceData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  // Save invoice data
  const saveInvoiceData = async (e) => {
    e.preventDefault();
    setInvoiceSubmitting(true);
    setSuccessMessage('');
    setErrors({});

    // Validation
    const newErrors = {};
    if (!invoiceData.name) newErrors.name = 'Numele este obligatoriu';
    if (!invoiceData.address) newErrors.address = 'Adresa este obligatorie';
    if (!invoiceData.city) newErrors.city = 'Orașul este obligatoriu';
    if (!invoiceData.subdivision_code) newErrors.subdivision_code = 'Județul este obligatoriu';

    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      setInvoiceSubmitting(false);
      return;
    }

    try {
      const idToken = await auth.currentUser.getIdToken(true);

      // Prepare invoice data payload, excluding bank and iban for individuals
      const invoicePayload = { ...invoiceData };
      if (formData.legalStatus !== 'LEGAL_ENTITY') {
        delete invoicePayload.bank;
        delete invoicePayload.iban;
        delete invoicePayload.vatPayer;
        delete invoicePayload.reg_com;
      }

      const response = await axios.patch('https://api.servicegest.ro/driver/update',
        { invoiceData: invoicePayload },
        {
          headers: {
            'Authorization': `Bearer ${idToken}`,
            'Content-Type': 'application/json'
          }
        }
      );

      if (response.status === 200) {
        await refreshDriverAccount();
        setSuccessMessage(t('settings.configuration.invoiceDataUpdated', 'Date de facturare actualizate cu succes'));
      }
    } catch (error) {
      console.error('Error updating invoice data:', error);
      setErrors({
        submit: t('settings.configuration.errorMessage')
      });
    } finally {
      setInvoiceSubmitting(false);
    }
  };

  return (
    <div className="bg-white dark:bg-slate-800 rounded-xl shadow-sm border border-slate-200 dark:border-slate-700">
      {/* Header */}
      <div className="px-2 py-2 border-b border-slate-200 dark:border-slate-700 bg-purple-50 dark:bg-purple-900/20 rounded-t-xl">
        <div className="flex items-center space-x-3">
          <div className="p-2 bg-purple-100 dark:bg-purple-900/30 rounded-lg">
            <Cog6ToothIcon className="w-5 h-5 text-purple-600 dark:text-purple-400" />
          </div>
          <div>
            <h3 className="text-lg font-semibold text-slate-900 dark:text-white">
              {t('settings.configuration.title')}
            </h3>
          </div>
        </div>
      </div>

      <div className="p-5">
        {/* Success/Error messages for config changes */}
        {successMessage && (
          <div className="mb-4 p-3 bg-green-50 dark:bg-green-900/20 text-green-700 dark:text-green-400 rounded-md flex items-start">
            <CheckCircleIcon className="h-5 w-5 mr-2 flex-shrink-0" />
            <span>{successMessage}</span>
          </div>
        )}

        <div className="grid grid-col s-1 lg:grid-cols-2 gap-6">
          {/* General Settings */}
          <div>
            <h4 className="text-sm font-medium mb-3 text-slate-700 dark:text-slate-300 border-b border-slate-200 dark:border-slate-700 pb-2 flex items-center">
              <Cog6ToothIcon className="h-4 w-4 mr-2 text-purple-500 dark:text-purple-400" />
              {t('settings.configuration.general.title', 'General')}
            </h4>

            {configSuccessMessage && (
              <div className="mb-4 p-3 bg-green-50 dark:bg-green-900/20 text-green-700 dark:text-green-400 rounded-md flex items-start">
                <CheckCircleIcon className="h-5 w-5 mr-2 flex-shrink-0" />
                <span>{configSuccessMessage}</span>
              </div>
            )}

            {configErrors.config && (
              <div className="mb-4 p-3 bg-red-50 dark:bg-red-900/20 text-red-700 dark:text-red-400 rounded-md flex items-start">
                <ExclamationCircleIcon className="h-5 w-5 mr-2 flex-shrink-0" />
                <span>{configErrors.config}</span>
              </div>
            )}

            <div className="space-y-4">
              <LanguageSwitcher />

              {/* Mobile Navigation Style Selector */}
              <div>
                <label className="flex items-center text-sm font-medium text-slate-700 dark:text-slate-300 mb-2">
                  <DevicePhoneMobileIcon className="h-4 w-4 mr-2 text-purple-500 dark:text-purple-400" />
                  {t('settings.configuration.mobileNavigation.title', 'Mobile Navigation Style')}
                </label>
                <FormControl fullWidth variant="outlined" size="small">
                  <Select
                    value={mobileNavStyle}
                    onChange={handleMobileNavChange}
                    className="bg-white dark:bg-slate-700 dark:text-white"
                  >
                    <MenuItem value="hamburger">
                      <div className="flex items-center">
                        <span>{t('settings.configuration.mobileNavigation.hamburger', 'Hamburger Menu (Sidebar)')}</span>
                      </div>
                    </MenuItem>
                    <MenuItem value="bottom">
                      <div className="flex items-center">
                        <span>{t('settings.configuration.mobileNavigation.bottom', 'Bottom Navigation Bar')}</span>
                      </div>
                    </MenuItem>
                  </Select>
                </FormControl>
                <p className="mt-2 text-xs text-slate-500 dark:text-slate-400">
                  {t('settings.configuration.mobileNavigation.description', 'Choose how you want to navigate on mobile devices')}
                </p>
              </div>

              <div>
                <h4 className="text-sm font-medium mb-3 text-slate-700 dark:text-slate-300 border-b border-slate-200 dark:border-slate-700 pb-2 flex items-center">
                  <BuildingOfficeIcon className="h-4 w-4 mr-2 text-purple-500 dark:text-purple-400" />
                  {t('settings.configuration.legalStatus.title')}
                </h4>

                {formData.legalStatus === 'UNDEFINED' && (
                  <div className="mb-3 p-2 bg-yellow-50 dark:bg-yellow-900/20 rounded-md">
                    <p className="text-amber-700 dark:text-amber-400 text-sm flex items-center">
                      <ExclamationCircleIcon className="h-4 w-4 mr-1 flex-shrink-0" />
                      {t('settings.configuration.legalStatus.warning')}
                    </p>
                  </div>
                )}

                <FormControl fullWidth variant="outlined" size="small">
                  <InputLabel id="legal-status-label"
                    className='dark:text-white'>
                    {formData.legalStatus === 'UNDEFINED'
                      ? t('settings.configuration.legalStatus.select')
                      : t('settings.configuration.legalStatus.title')
                    }
                  </InputLabel>
                  <Select
                    labelId="legal-status-label"
                    id="legal-status"
                    value={formData.legalStatus === 'UNDEFINED' ? '' : getUILegalStatus(formData.legalStatus)}
                    onChange={handleLegalStatusChange}
                    label={formData.legalStatus === 'UNDEFINED'
                      ? t('settings.configuration.legalStatus.select')
                      : t('settings.configuration.legalStatus.title')
                    }
                    className="bg-white dark:bg-slate-700 dark:text-white"
                  >
                    <MenuItem value="persoana_fizica">{getLegalStatusDisplay('INDIVIDUAL')}</MenuItem>
                    <MenuItem value="persoana_juridica">{getLegalStatusDisplay('LEGAL_ENTITY')}</MenuItem>
                  </Select>
                </FormControl>
              </div>
            </div>
          </div>

          {/* Integrations */}
          <div>
            <h4 className="text-sm font-medium mb-3 text-slate-700 dark:text-slate-300 border-b border-slate-200 dark:border-slate-700 pb-2 flex items-center">
              <CalendarIcon className="h-4 w-4 mr-2 text-purple-500 dark:text-purple-400" />
              {t('settings.configuration.integrations.title')}
            </h4>

            <div className="space-y-4">
              {/* Information Sharing Checkbox */}
              <div className="flex items-center bg-slate-50 dark:bg-slate-700/50 p-3 rounded-md">
                <input
                  type="checkbox"
                  id="shareInformation"
                  name="shareInformation"
                  checked={formData.shareInformation}
                  onChange={handleInformationSharingChange}
                  className="h-4 w-4 text-purple-600 focus:ring-purple-500 border-gray-300 rounded"
                />
                <label htmlFor="shareInformation" className="ml-2 block text-sm text-slate-700 dark:text-slate-300">
                  {t('settings.configuration.integrations.shareInfo')}
                </label>
              </div>
            </div>
          </div>
        </div>

        {/* Invoice Data Section */}
        {driverAccount?.invoiceData && (
          <div className="mt-6 border-t border-slate-200 dark:border-slate-700 pt-6">
            <h4 className="text-sm font-medium mb-4 text-slate-700 dark:text-slate-300 flex items-center">
              <DocumentTextIcon className="h-4 w-4 mr-2 text-purple-500 dark:text-purple-400" />
              {t('settings.configuration.invoiceData.title')}
            </h4>

            {successMessage && (
              <div className="mb-4 p-3 bg-green-50 dark:bg-green-900/20 text-green-700 dark:text-green-400 rounded-md flex items-start">
                <CheckCircleIcon className="h-5 w-5 mr-2 flex-shrink-0" />
                <span>{successMessage}</span>
              </div>
            )}

            {errors.submit && (
              <div className="mb-4 p-3 bg-red-50 dark:bg-red-900/20 text-red-700 dark:text-red-400 rounded-md flex items-start">
                <ExclamationCircleIcon className="h-5 w-5 mr-2 flex-shrink-0" />
                <span>{errors.submit}</span>
              </div>
            )}

            <form onSubmit={saveInvoiceData} className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-1 gap-4">
                <div>
                  <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">
                    {formData.legalStatus === 'LEGAL_ENTITY' 
                      ? t('settings.configuration.invoiceData.companyName') 
                      : t('settings.configuration.invoiceData.fullName')
                    } {t('settings.configuration.invoiceData.requiredField')}
                  </label>
                  <input
                    type="text"
                    value={invoiceData.name}
                    onChange={(e) => handleInvoiceDataChange('name', e.target.value)}
                    className={`w-full px-3 py-2 rounded-lg border ${errors.name ? 'border-red-500' : 'border-slate-300 dark:border-slate-600'} bg-white dark:bg-slate-900 text-slate-900 dark:text-white focus:border-purple-600 dark:focus:border-purple-400 focus:outline-none`}
                  />
                  {errors.name && <p className="mt-1 text-xs text-red-600 dark:text-red-400">{errors.name}</p>}
                </div>

                <div>
                  <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">
                    {formData.legalStatus === 'LEGAL_ENTITY' 
                      ? t('settings.configuration.invoiceData.cui') 
                      : t('settings.configuration.invoiceData.cnp')
                    }
                  </label>
                  <input
                    type="text"
                    value={invoiceData.tax_identifier}
                    onChange={(e) => handleInvoiceDataChange('tax_identifier', e.target.value)}
                    className={`w-full px-3 py-2 rounded-lg border ${errors.tax_identifier ? 'border-red-500' : 'border-slate-300 dark:border-slate-600'} bg-white dark:bg-slate-900 text-slate-900 dark:text-white focus:border-purple-600 dark:focus:border-purple-400 focus:outline-none`}
                  />
                  {errors.tax_identifier && <p className="mt-1 text-xs text-red-600 dark:text-red-400">{errors.tax_identifier}</p>}
                </div>

                {formData.legalStatus === 'LEGAL_ENTITY' && (
                  <div>
                    <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">
                      {t('settings.configuration.invoiceData.regCom')}
                    </label>
                    <input
                      type="text"
                      value={invoiceData.reg_com || ''}
                      onChange={(e) => handleInvoiceDataChange('reg_com', e.target.value)}
                      className="w-full px-3 py-2 rounded-lg border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-900 text-slate-900 dark:text-white focus:border-purple-600 dark:focus:border-purple-400 focus:outline-none"
                    />
                  </div>
                )}

                <div className={formData.legalStatus === 'LEGAL_ENTITY' ? '' : 'md:col-span-1'}>
                  <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">
                    {t('settings.configuration.invoiceData.address')} {t('settings.configuration.invoiceData.requiredField')}
                  </label>
                  <input
                    type="text"
                    value={invoiceData.address}
                    onChange={(e) => handleInvoiceDataChange('address', e.target.value)}
                    className={`w-full px-3 py-2 rounded-lg border ${errors.address ? 'border-red-500' : 'border-slate-300 dark:border-slate-600'} bg-white dark:bg-slate-900 text-slate-900 dark:text-white focus:border-purple-600 dark:focus:border-purple-400 focus:outline-none`}
                  />
                  {errors.address && <p className="mt-1 text-xs text-red-600 dark:text-red-400">{errors.address}</p>}
                </div>

                <div>
                  <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">
                    {t('settings.configuration.invoiceData.city')} {t('settings.configuration.invoiceData.requiredField')}
                  </label>
                  <input
                    type="text"
                    value={invoiceData.city}
                    onChange={(e) => handleInvoiceDataChange('city', e.target.value)}
                    className={`w-full px-3 py-2 rounded-lg border ${errors.city ? 'border-red-500' : 'border-slate-300 dark:border-slate-600'} bg-white dark:bg-slate-900 text-slate-900 dark:text-white focus:border-purple-600 dark:focus:border-purple-400 focus:outline-none`}
                  />
                  {errors.city && <p className="mt-1 text-xs text-red-600 dark:text-red-400">{errors.city}</p>}
                </div>

                <div>
                  <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">
                    {t('settings.configuration.invoiceData.county')} {t('settings.configuration.invoiceData.requiredField')}
                  </label>
                  <FormControl fullWidth variant="outlined" size="small" error={!!errors.subdivision_code}>
                    <Select
                      value={invoiceData.subdivision_code}
                      onChange={(e) => handleInvoiceDataChange('subdivision_code', e.target.value)}
                      className={`bg-white dark:bg-slate-900 ${errors.subdivision_code ? 'border-red-500' : ''}`}
                      displayEmpty
                    >
                      <MenuItem value="" disabled>
                        <span className="text-slate-400">{t('settings.configuration.general.selectCounty')}</span>
                      </MenuItem>
                      {romanianCounties.map((county) => (
                        <MenuItem key={county.code} value={county.code}>
                          {county.name} ({county.code})
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                  {errors.subdivision_code && <p className="mt-1 text-xs text-red-600 dark:text-red-400">{errors.subdivision_code}</p>}
                </div>

                {formData.legalStatus === 'LEGAL_ENTITY' && (
                  <>
                    <div>
                      <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">
                        {t('settings.configuration.invoiceData.bank')}
                      </label>
                      <input
                        type="text"
                        value={invoiceData.bank || ''}
                        onChange={(e) => handleInvoiceDataChange('bank', e.target.value)}
                        className="w-full px-3 py-2 rounded-lg border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-900 text-slate-900 dark:text-white focus:border-purple-600 dark:focus:border-purple-400 focus:outline-none"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">
                        {t('settings.configuration.invoiceData.iban')}
                      </label>
                      <input
                        type="text"
                        value={invoiceData.iban || ''}
                        onChange={(e) => handleInvoiceDataChange('iban', e.target.value)}
                        placeholder="RO49AAAA1B31007593840000"
                        className="w-full px-3 py-2 rounded-lg border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-900 text-slate-900 dark:text-white focus:border-purple-600 dark:focus:border-purple-400 focus:outline-none"
                      />
                    </div>

                    <div className="md:col-span-2">
                      <label className="flex items-center">
                        <input
                          type="checkbox"
                          checked={invoiceData.vatPayer}
                          onChange={(e) => handleInvoiceDataChange('vatPayer', e.target.checked)}
                          className="h-4 w-4 text-purple-600 focus:ring-purple-500 border-gray-300 rounded"
                        />
                        <span className="ml-2 text-sm text-slate-700 dark:text-slate-300">
                          {t('settings.configuration.invoiceData.vatPayer')}
                        </span>
                      </label>
                    </div>
                  </>
                )}
              </div>
              <div className="flex justify-end pt-2">

                <button
                  type="submit"
                  disabled={invoiceSubmitting}
                  className="px-4 py-2 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-purple-600 hover:bg-purple-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-purple-500 dark:focus:ring-offset-gray-800 disabled:opacity-70 disabled:cursor-not-allowed transition-colors"
                >
                  {invoiceSubmitting ? t('settings.configuration.invoiceData.saving') : t('settings.configuration.invoiceData.saveButton')}
                </button>
              </div>
            </form>
          </div>
        )}
      </div>
    </div>
  );
};

export default ConfigurationSection;
